import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import PageLayout from "./components/PageLayout";
import BuildBinder from "./components/BuildBinder";
import {
  normalizeCarStages,
  getCurrentStage
} from "./utils/buildStages";

function BuildBinderPage() {
  const { username, carId } = useParams();
  const navigate = useNavigate();

  const [authResolved, setAuthResolved] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [car, setCar] = useState(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);

  const shouldOpenUpload =
    new URLSearchParams(window.location.search).get("upload") === "1";

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthResolved(true);

      if (!user) {
        navigate("/auth");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const loadBuildBinderPage = async () => {
      if (!authResolved) return;

      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        if (!username || !carId) {
          setPageError("Page unavailable.");
          setLoading(false);
          return;
        }

        setLoading(true);
        setPageError("");

        const profileRef = doc(db, "profiles", username.toLowerCase());
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
          setPageError("Page unavailable.");
          setLoading(false);
          return;
        }

        const profileData = profileSnap.data();
        const foundCar = profileData.garageCars?.find(
          (item) => String(item.id) === String(carId)
        );

        if (!foundCar) {
          setPageError("Page unavailable.");
          setLoading(false);
          return;
        }

        if (currentUser.uid !== profileData.userId) {
          navigate(`/user/${username}/car/${carId}`);
          return;
        }

        const normalizedCar = normalizeCarStages(foundCar);
        getCurrentStage(normalizedCar);

        setProfile({
          ...profileData,
          username: profileData.username || username
        });
        setCar(normalizedCar);
      } catch (error) {
        console.log("Build Binder page load failed:", error);
        setPageError("Page unavailable.");
      } finally {
        setLoading(false);
      }
    };

    loadBuildBinderPage();
  }, [authResolved, currentUser, username, carId, navigate]);

  if (loading || !authResolved) {
    return (
      <PageLayout maxWidth="760px">
        <div style={loadingStyle}>Loading...</div>
      </PageLayout>
    );
  }

  if (pageError || !profile || !car) {
    return (
      <PageLayout maxWidth="760px">
        <div style={unavailableStyle}>Page unavailable.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="760px">
      <button
        type="button"
        onClick={() => navigate("/garage")}
        style={backButtonStyle}
      >
        ← Back to Garage
      </button>

      <BuildBinder
        ownerId={profile.userId}
        username={profile.username}
        carId={String(car.id)}
        isOwner={true}
        initialUploadOpen={shouldOpenUpload}
        maintenanceItems={
          getCurrentStage(car)?.maintenance ||
          car.maintenance ||
          []
        }
        mods={
          getCurrentStage(car)?.mods ||
          car.mods ||
          []
        }
      />
    </PageLayout>
  );
}

const loadingStyle = {
  color: "white",
  textAlign: "center",
  padding: "40px"
};

const unavailableStyle = {
  color: "white",
  textAlign: "center",
  padding: "40px",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "16px"
};

const backButtonStyle = {
  backgroundColor: "#181818",
  color: "white",
  border: "1px solid #2a2a2a",
  borderRadius: "999px",
  padding: "10px 14px",
  fontWeight: "bold",
  cursor: "pointer",
  marginBottom: "18px"
};

export default BuildBinderPage;